import {apiHelpers, errorHandler} from '@/ft-vue-core'
import LockTimer from '@/ft-vue-core/components/LockTimer'
import KeyRefresher from '@/ft-vue-core/components/helpers/KeyRefresher'
export default {
  components: {LockTimer, KeyRefresher},
  mixins: [apiHelpers, errorHandler],
  data () {
    return {}
  },
  methods: {
    /**
     * Pass-though method that will inject record-locking configurations into an API 
     * Command object
     * @param {Object} command API Command Object to be returned with locking enabled
     */
    withResourceLockHandler (command) {
      const lockConfig = {lock: true, include_hash: true}
      if (command.params) command.params = Object.assign(command.params, lockConfig)
      return command
    },
    /**
     * Configured a LockTimer settings object with default release/renew on 
     * @param {Array} records Locked records that can berepresented by 1 LockTimer
     * @returns {Object} LockTimer configuration object
     */
    getLockTimerSettings (records, renewParams = {}, onAccept = () => {}) {
      if (!records?.length) return {}
      const [r] = records
      return {
        endTime: r.recordLockExpiration,
        onTimeout: () => this.releaseLock(records),
        onAccept: (dialog) => {
          onAccept()
          dialog?.close()
        },
        renewCb: (dialog) => this.renewLock(records, renewParams, dialog)
      }
    },
    /**
     * Renew the locks of records passed in.
     * 
     * NOTE: INCLUDE `renewParams` TO PROVIDE CORRECT RECORD HASH. 
     * `recordType` is required, `include_fields`, `exclude_fields` and `children` 
     * parameters must match original API command.
     * 
     * NOTE: ENSURE YOUR ORIGINAL REQUEST INCLUDED `include_hash: true` PARAMETER
     * @param {Array} records Records whose locks need to be renewed
     * @param {Object} renewParams Params object whose contents will be used to ensure record hashes match
     * @param {Object} dialog Disalog object provided by LockTimer's `renewCb` callback
     * @returns {Array} Records whose locks have been renewed
     */
    async renewLock (records = [], renewParams = {}, dialog) {
      if (!renewParams.recordType) return new Array()
      const {recordType: record_type} = renewParams
      await Promise.all(records.map(r => {
        return new Promise((resolve, reject) => {
          const {recordLockKey: record_key, recordHash: record_hash, id} = r
          this.$ftAPI().get({
            command: 'renewLock',
            loading: this.setIsLoading,
            load (resp) {
              const {key: recordLockKey, expiration: recordLockExpiration} = resp
              this.setRecordLocksInObject(r, r.recordLockKey, recordLockKey, recordLockExpiration)
              resolve(r)
            },
            error: (err) => reject(err),
            params: Object.assign({record_key, record_hash, id, record_type}, renewParams)
          })
        })
      })).then(r => {
        dialog?.close()
        return r
      }).catch(err => {
        if (Array.isArray(err)) this.errors.push(...err.map(e => e.message))
        else this.errors.push(err.message)
        return new Array()
      })
    },
    /**
     * Release the locks of records passed in.
     * @see getLockTimerSettings Called in onTimeout
     * @param {Array} records Instance of all locked records that will be released
     * @param {Function} success Optional success callback function
     * @returns {Boolean} Success/Fail status of API response
     */
    releaseLock (records = [], success) {
      if (!records?.length) return false
      this.$ftAPI().get({
        command: 'releaseLock',
        load: (r) => success?.(r),
        afterLoad: () => {return true},
        error: () => {return false},
        params: {record_key: records.map(r => r.recordLockKey)}
      })
    },
    /**
     * Set the record locks of an API Record.
     * @param {Object} record API Record object to set locks in
     * @param {String} oldKey Old recordLockKey value
     * @param {String} newKey New recordLockKey value
     * @param {String} expiration recordLockExpiration value
     * @see itemEdit.js
     */
    setRecordLocksInObject (record, oldKey, recordLockKey, recordLockExpiration) {
      if (!record) return
      if (Array.isArray(record)) {
        for (const r of record) {
          this.setRecordLocksInObject(r, oldKey, recordLockKey, recordLockExpiration)
        }
      }
      for (const key in record) {
        const value = record[key]
        if (key === 'recordLockKey' && (value === oldKey || !oldKey && !value))
          record = Object.assign(record, {recordLockKey, recordLockExpiration})
        else if (typeof value === 'object')
          this.setRecordLocksInObject(value, oldKey, recordLockKey, recordLockExpiration)
      }
    },
  }
}